//
// ActionEditor.tsx — the detail panel shown when an action is
// selected on the timeline. Shows type-specific target fields
// plus the common time/easing controls and a Delete button.
//
// Every change dispatches a freshly-built action through onUpdate.
// The component doesn't hold its own state — it reads the parent's
// action and writes back on every edit.
//
import type { JSX } from 'preact';
import {
  type Action,
  type Easing,
  type PanAction,
  type PitchAction,
  type RotateAction,
  type ZoomAction,
} from '../timeline';
import {
  buttonStyle,
  editorBoxStyle,
  labelSpanStyle,
  rowStyle,
  selectStyle,
  smallInputStyle,
} from './styles';

export interface ActionEditorProps {
  action: Action;
  //
  // Called with a fresh action whenever any field changes. The
  // Panel is responsible for replacing the old action in its list.
  //
  onUpdate: (action: Action) => void;
  //
  // Called when the user clicks Delete. Parent removes the action.
  //
  onDelete: () => void;
  //
  // Called when the user clicks a "Capture current view" button.
  // Parent snapshots the live map state and updates the relevant
  // target field on the action. Passed down rather than handled
  // here because this component has no reference to the Mapbox
  // map instance.
  //
  onCaptureTarget: () => void;
}

const EASING_OPTIONS: Easing[] = ['linear', 'ease-in', 'ease-out', 'ease-in-out'];

export function ActionEditor(props: ActionEditorProps): JSX.Element {
  const { action, onUpdate, onDelete, onCaptureTarget } = props;

  return (
    <div style={editorBoxStyle}>
      <div style={{ ...rowStyle, marginTop: 0, justifyContent: 'space-between' }}>
        <div style={{ fontWeight: '700', color: '#e5e7eb' }}>{typeLabel(action)}</div>
        <button style={buttonStyle({ compact: true, danger: true })} onClick={onDelete}>
          Delete
        </button>
      </div>

      <TargetFields action={action} onUpdate={onUpdate} onCaptureTarget={onCaptureTarget} />

      <div style={rowStyle}>
        <span style={labelSpanStyle}>Start</span>
        <NumberInput
          value={action.start}
          step={0.05}
          min={0}
          onChange={(v) => onUpdate({ ...action, start: v, end: Math.max(v + 0.2, action.end) } as Action)}
        />
        <span style={labelSpanStyle}>Duration</span>
        <NumberInput
          value={action.end - action.start}
          step={0.05}
          min={0.2}
          onChange={(v) => onUpdate({ ...action, end: action.start + v } as Action)}
        />
        <span style={labelSpanStyle}>Easing</span>
        <select
          value={action.easing}
          onChange={(e) =>
            onUpdate({ ...action, easing: (e.currentTarget as HTMLSelectElement).value as Easing } as Action)
          }
          style={selectStyle}
          disabled={!!action.smooth}
          title={action.smooth ? 'Easing is ignored inside smooth groups — the spline carries the smoothness itself.' : undefined}
        >
          {EASING_OPTIONS.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
      </div>
      <div style={rowStyle}>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
          title="Consecutive smooth actions on the same track with no gap between them are joined into a single Catmull-Rom spline. A lone smooth action degenerates to linear."
        >
          <input
            type="checkbox"
            checked={!!action.smooth}
            onChange={(e) =>
              onUpdate({ ...action, smooth: (e.currentTarget as HTMLInputElement).checked } as Action)
            }
          />
          <span style={labelSpanStyle}>Smooth (spline with neighbouring smooth actions)</span>
        </label>
      </div>
    </div>
  );
}

//
// Per-type target fields + "Capture current view" button. Branches
// on the discriminated union so each variant types cleanly.
//
function TargetFields(props: {
  action: Action;
  onUpdate: (action: Action) => void;
  onCaptureTarget: () => void;
}): JSX.Element {
  const { action, onUpdate, onCaptureTarget } = props;

  if (action.type === 'pan') {
    const a = action as PanAction;
    return (
      <div style={rowStyle}>
        <span style={labelSpanStyle}>Lat</span>
        <NumberInput
          value={a.target.lat}
          step={0.0001}
          onChange={(v) => onUpdate({ ...a, target: { ...a.target, lat: v } })}
        />
        <span style={labelSpanStyle}>Lng</span>
        <NumberInput
          value={a.target.lng}
          step={0.0001}
          onChange={(v) => onUpdate({ ...a, target: { ...a.target, lng: v } })}
        />
        <button style={buttonStyle({ compact: true })} onClick={onCaptureTarget}>
          Capture from map
        </button>
      </div>
    );
  }

  if (action.type === 'zoom') {
    const a = action as ZoomAction;
    return (
      <div style={rowStyle}>
        <span style={labelSpanStyle}>Target zoom</span>
        <NumberInput
          value={a.target}
          step={0.1}
          min={0}
          max={22}
          onChange={(v) => onUpdate({ ...a, target: v })}
        />
        <button style={buttonStyle({ compact: true })} onClick={onCaptureTarget}>
          Capture from map
        </button>
      </div>
    );
  }

  if (action.type === 'pitch') {
    const a = action as PitchAction;
    return (
      <div style={rowStyle}>
        <span style={labelSpanStyle}>Target pitch</span>
        <NumberInput
          value={a.target}
          step={1}
          min={0}
          max={85}
          onChange={(v) => onUpdate({ ...a, target: v })}
        />
        <span style={labelSpanStyle}>°</span>
        <button style={buttonStyle({ compact: true })} onClick={onCaptureTarget}>
          Capture from map
        </button>
      </div>
    );
  }

  //
  // Rotate — has two modes, each with a distinct target field.
  // Switching mode rebuilds the action from scratch to satisfy
  // the discriminated union.
  //
  const a = action as RotateAction;
  const switchMode = (mode: 'absolute' | 'delta'): void => {
    if (mode === a.mode) return;
    if (mode === 'absolute') {
      onUpdate({ id: a.id, type: 'rotate', mode: 'absolute', start: a.start, end: a.end, easing: a.easing, target: 0 });
    } else {
      onUpdate({ id: a.id, type: 'rotate', mode: 'delta', start: a.start, end: a.end, easing: a.easing, deltaDegrees: 360 });
    }
  };

  return (
    <>
      <div style={rowStyle}>
        <span style={labelSpanStyle}>Mode</span>
        <select
          value={a.mode}
          onChange={(e) => switchMode((e.currentTarget as HTMLSelectElement).value as 'absolute' | 'delta')}
          style={selectStyle}
        >
          <option value="absolute">absolute heading</option>
          <option value="delta">relative degrees</option>
        </select>
      </div>
      {a.mode === 'absolute' ? (
        <div style={rowStyle}>
          <span style={labelSpanStyle}>Target bearing</span>
          <NumberInput
            value={a.target}
            step={1}
            onChange={(v) => onUpdate({ ...a, target: v })}
          />
          <span style={labelSpanStyle}>°</span>
          <button style={buttonStyle({ compact: true })} onClick={onCaptureTarget}>
            Capture from map
          </button>
        </div>
      ) : (
        <div style={rowStyle}>
          <span style={labelSpanStyle}>Delta</span>
          <NumberInput
            value={a.deltaDegrees}
            step={30}
            onChange={(v) => onUpdate({ ...a, deltaDegrees: v })}
          />
          <span style={labelSpanStyle}>° (positive = clockwise)</span>
        </div>
      )}
    </>
  );
}

//
// Human-readable type label for the editor heading.
//
function typeLabel(action: Action): string {
  switch (action.type) {
    case 'pan': return 'Pan';
    case 'zoom': return 'Zoom';
    case 'pitch': return 'Pitch';
    case 'rotate': return action.mode === 'delta' ? 'Rotate (delta)' : 'Rotate (to heading)';
  }
}

//
// Small numeric input with sensible defaults. Calls onChange with
// a parsed number only when the value is finite — keeps the parent
// from seeing NaN while the user is mid-edit.
//
function NumberInput(props: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}): JSX.Element {
  return (
    <input
      type="number"
      min={props.min}
      max={props.max}
      step={props.step ?? 1}
      value={props.value}
      onInput={(e) => {
        const raw = (e.currentTarget as HTMLInputElement).value;
        const n = Number(raw);
        if (Number.isFinite(n)) props.onChange(n);
      }}
      style={smallInputStyle}
    />
  );
}
